import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { IMetaData } from '../../interfaces/meta-data.interface';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, firstValueFrom, of } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class MetasService {
  #titleService: Title = inject(Title);
  #meta: Meta = inject(Meta);
  #translateService: TranslateService = inject(TranslateService);
  #platformId = inject(PLATFORM_ID);
  #document = inject(DOCUMENT);

  private readonly baseUrl = 'https://braidsbook.com';
  private readonly defaultImage = 'https://braidsbook.com/images/smiling-woman-holding-afro-picks.webp';

  /**
   * Update meta tags with translations (async for SSR compatibility)
   * Supports both translation keys (starting with 'SEO.') and direct strings
   * @param metaData - Meta data to update
   * @param params - Optional translation parameters
   */
  async updateMetaTags(metaData: IMetaData, params: any = {}): Promise<void> {
    const titleKey = metaData.title;
    const descriptionKey = metaData.description;
    const keywordsKey = metaData.keywords;

    // Check if keys are translation keys (contain '.') or direct strings
    const isTitleKey = titleKey.includes('.');
    const isDescriptionKey = descriptionKey.includes('.');
    const isKeywordsKey = keywordsKey?.includes('.');

    // Get translations or use direct strings with a timeout to prevent hanging during prerendering
    const translations = await firstValueFrom(
      forkJoin({
        title: isTitleKey ? this.#translateService.get(titleKey, params) : of(titleKey),
        description: isDescriptionKey ? this.#translateService.get(descriptionKey, params) : of(descriptionKey),
        keywords: keywordsKey ? (isKeywordsKey ? this.#translateService.get(keywordsKey, params) : of(keywordsKey)) : of('')
      }).pipe(
        // Si les traductions mettent trop de temps (ex: SSR), on utilise les clés comme fallback
        timeout(2000),
        catchError(() => of({
          title: titleKey,
          description: descriptionKey,
          keywords: keywordsKey || ''
        }))
      )
    );

    const fullUrl = metaData.url.startsWith('http') ? metaData.url : `${this.baseUrl}${metaData.url}`;
    const imageUrl = metaData.image || this.defaultImage;

    // Update title (works on both server and client)
    this.#titleService.setTitle(translations.title);

    // Update meta tags (works on both server and client for SSR)
    this.updateMetaTag({ name: 'title', content: translations.title });
    this.updateMetaTag({ name: 'description', content: translations.description });

    if (translations.keywords) {
      this.updateMetaTag({ name: 'keywords', content: translations.keywords });
    }

    // Open Graph tags
    this.updateMetaTag({ property: 'og:type', content: metaData.type || 'website' });
    this.updateMetaTag({ property: 'og:title', content: translations.title });
    this.updateMetaTag({ property: 'og:description', content: translations.description });
    this.updateMetaTag({ property: 'og:url', content: fullUrl });
    this.updateMetaTag({ property: 'og:image', content: imageUrl });
    this.updateMetaTag({ property: 'og:image:width', content: '1200' });
    this.updateMetaTag({ property: 'og:image:height', content: '630' });

    // Twitter Card tags
    this.updateMetaTag({ property: 'twitter:card', content: 'summary_large_image' });
    this.updateMetaTag({ property: 'twitter:title', content: translations.title });
    this.updateMetaTag({ property: 'twitter:description', content: translations.description });
    this.updateMetaTag({ property: 'twitter:image', content: imageUrl });
    this.updateMetaTag({ property: 'twitter:url', content: fullUrl });

    // Canonical link
    this.updateCanonicalTag(fullUrl);

    // Robots meta tag
    if (metaData.robots) {
      this.updateMetaTag({ name: 'robots', content: metaData.robots });
    } else {
      // If not specified, we can either leave it to browser default or remove if it was set
      this.#meta.removeTag('name="robots"');
    }

    // Structured Data (JSON-LD)
    if (metaData.structuredData) {
      this.updateStructuredData(metaData.structuredData);
    }
  }

  /**
   * Update or create a canonical link tag
   *
   * Une URL relative est complétée avec le domaine : la balise canonique sert
   * à désigner l'adresse de référence d'une page, et une valeur relative se
   * résout contre la page courante — ce qui la rend inopérante dès que la même
   * page est atteinte par une autre URL, précisément le cas qu'elle doit régler.
   */
  updateCanonicalTag(url: string): void {
    let link: HTMLLinkElement | null = this.#document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.#document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.#document.head.appendChild(link);
    }
    link.setAttribute('href', url.startsWith('http') ? url : `${this.baseUrl}${url}`);
  }

  /**
   * Inject JSON-LD structured data into the head
   */
  updateStructuredData(data: any | any[]): void {
    // Remove existing JSON-LD if present to avoid duplication
    const existingScripts = this.#document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => this.#document.head.removeChild(script));

    const dataArray = Array.isArray(data) ? data : [data];

    dataArray.forEach(item => {
      const script = this.#document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(item);
      this.#document.head.appendChild(script);
    });
  }

  /**
   * Update or create a meta tag
   */
  private updateMetaTag(tag: { name?: string; property?: string; content: string }): void {
    this.#meta.updateTag(tag);
  }

  /**
   * Update language attribute in HTML tag (for SSR)
   */
  updateLanguage(lang: string): void {
    if (isPlatformBrowser(this.#platformId)) {
      document.documentElement.lang = lang;
    }
  }
}
